import {Icon} from '@/app/components/Icon';
import {UserAvatar} from '@/components/app/feedback';
import {Button} from '@/components/app/button';
import {ChoiceSelect} from '@/components/app/choice-select';
import {cn} from '@/lib/utils';
import {BiotwinShare, BiotwinSharePrivilege, BiotwinShareStatus} from '../types';
import {useSession} from '../../auth/hooks/useSession';
import {useDeleteBiotwinShareMutation, useSetBiotwinShareStatusMutation} from '../../../app/services/biotwinShareApi';
export interface BiotwinShareInfoProps {
  biotwinShare: BiotwinShare;
  className?: string;
  noAvatar?: boolean;
  noBiotwinName?: boolean;
  noDate?: boolean;
  noMessage?: boolean;
  noName?: boolean;
  onAccept?: () => void;
  onCancel?: () => void;
  onIgnore?: () => void;
  onOptOut?: () => void;
  onStop?: () => void;
  readOnly?: boolean;
}
export const BiotwinShareInfo = ({
  biotwinShare,
  className,
  noAvatar,
  noBiotwinName,
  noDate,
  noMessage,
  noName,
  onAccept,
  onCancel,
  onIgnore,
  onOptOut,
  onStop,
  readOnly,
}: BiotwinShareInfoProps) => {
  const [deleteBiotwinShare, {isLoading: isLoadingDeleteBiotwinShare}] = useDeleteBiotwinShareMutation();
  const [
    setBiotwinShareStatus,
    {isLoading: isLoadingSetBiotwinShareStatus, originalArgs: originalArgsSetBiotwinShare},
  ] = useSetBiotwinShareStatusMutation();
  const session = useSession();
  const incoming = biotwinShare.ownerId !== Number(session?.id);
  const name = incoming ? (biotwinShare.ownerDisplayName ?? biotwinShare.ownerName) : biotwinShare.email;
  return (
    <div className={cn(className, 'flex flex-wrap items-center gap-4')}>
      {!noAvatar && (
        <UserAvatar
          className={'shrink-0'}
          label={name
            .split(' ')
            .map((word) => word[0])
            .join('')}
          size={'xlarge'}
          shape={'circle'}
        />
      )}

      <div className={'flex flex-col flex-1 gap-1'}>
        {!noName && <strong>{name}</strong>}
        {!noBiotwinName && <span>{biotwinShare.biotypeName}</span>}
        {!noDate && (
          <time className={'text-muted-foreground text-sm'} dateTime={biotwinShare.createdAt}>
            {new Date(biotwinShare.createdAt).toLocaleString()}
          </time>
        )}
        {!noMessage && biotwinShare.message && <q className={'mt-1'}>{biotwinShare.message}</q>}
      </div>

      {incoming && biotwinShare.status === BiotwinShareStatus.Pending && !readOnly && (
        <div className={'flex items-center gap-4 ml-auto'}>
          <Button
            className={cn('text-muted-foreground', 'rounded-full')}
            isLoading={
              isLoadingSetBiotwinShareStatus && originalArgsSetBiotwinShare?.status === BiotwinShareStatus.Rejected
            }
            onClick={() => ignore()}
            type={'button'}
            variant="ghost"
          >
            {'Ignore'}
          </Button>
          <Button
            isLoading={
              isLoadingSetBiotwinShareStatus && originalArgsSetBiotwinShare?.status === BiotwinShareStatus.Accepted
            }
            onClick={() => accept()}
            type={'button'}
            className="rounded-full"
          >
            {'Accept'}
          </Button>
        </div>
      )}

      {incoming && biotwinShare.status === BiotwinShareStatus.Accepted && !readOnly && (
        <div className={'flex items-center gap-4 ml-auto'}>
          <Button
            className={cn('text-muted-foreground', 'rounded-full')}
            isLoading={isLoadingSetBiotwinShareStatus}
            onClick={() => optOut()}
            type={'button'}
            variant="ghost"
          >
            {'Remove'}
          </Button>
        </div>
      )}

      {!incoming && biotwinShare.status === BiotwinShareStatus.Pending && (
        <div className={'flex items-center gap-4 ml-auto'}>
          <span className={'text-muted-foreground'}>Pending...</span>
          {!readOnly && (
            <Button
              isLoading={isLoadingDeleteBiotwinShare}
              onClick={() => cancel()}
              type={'button'}
              variant="warning"
              className="rounded-full"
            >
              {'Cancel'}
            </Button>
          )}
        </div>
      )}

      {!incoming && biotwinShare.status === BiotwinShareStatus.Accepted && (
        <div className={'flex items-center gap-4 ml-auto'}>
          <ChoiceSelect
            readOnly={readOnly}
            itemTemplate={(option) => {
              return (
                <div className="flex gap-2 items-center">
                  <Icon icon={option.icon} />
                  <span>{option.label}</span>
                </div>
              );
            }}
            valueTemplate={(option) => {
              return (
                <div className="flex gap-2 items-center">
                  <Icon icon={option.icon} />
                  <span>{option.label}</span>
                </div>
              );
            }}
            options={[
              {
                icon: 'eye',
                label: 'Can Read',
                value: BiotwinSharePrivilege.Read,
              },
              {
                icon: 'pencil',
                label: 'Can Write',
                value: BiotwinSharePrivilege.Write,
              },
            ]}
            optionLabel={'label'}
            optionValue={'value'}
            value={biotwinShare.privilege}
          />
          {!readOnly && (
            <Button
              isLoading={isLoadingDeleteBiotwinShare}
              onClick={() => stopSharing()}
              type={'button'}
              variant="warning"
              className="rounded-full"
            >
              {'Stop Sharing'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
  function accept() {
    if (onAccept == null) {
      setBiotwinShareStatus({
        id: biotwinShare.id,
        status: BiotwinShareStatus.Accepted,
      });
    } else {
      onAccept();
    }
  }
  function cancel() {
    if (onCancel == null) {
      deleteBiotwinShare(biotwinShare);
    } else {
      onCancel();
    }
  }
  function ignore() {
    if (onIgnore == null) {
      setBiotwinShareStatus({
        id: biotwinShare.id,
        status: BiotwinShareStatus.Rejected,
      });
    } else {
      onIgnore();
    }
  }
  function optOut() {
    if (onOptOut == null) {
      deleteBiotwinShare(biotwinShare);
    } else {
      onOptOut();
    }
  }
  function stopSharing() {
    if (onStop == null) {
      deleteBiotwinShare(biotwinShare);
    } else {
      onStop();
    }
  }
};
